import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n";
import AppShell from "@/components/AppShell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import ClientOpsPortal from "@/pages/clientops/ClientOpsPortal";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Reports from "@/pages/Reports";
import Orders from "@/pages/Orders";
import PlaceOrder from "@/pages/PlaceOrder";
import OrderDetail from "@/pages/OrderDetail";
import Shipments from "@/pages/Shipments";
import ShipmentDetail from "@/pages/ShipmentDetail";
import ClientConfirmations from "@/pages/ClientConfirmations";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Suppliers from "@/pages/Suppliers";
import Clients from "@/pages/Clients";
import UsersPage from "@/pages/Users";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import PurchaseOrderDetail from "@/pages/PurchaseOrderDetail";
import Catalogue from "@/pages/Catalogue";
import PublicCatalogue from "@/pages/PublicCatalogue";
import Stock from "@/pages/Stock";
import Schedule from "@/pages/Schedule";
import Quotes from "@/pages/Quotes";
import QuoteDetail from "@/pages/QuoteDetail";
import StockIntake from "@/pages/StockIntake";
import Dispatch from "@/pages/Dispatch";
import DispatchDetail from "@/pages/DispatchDetail";
import DispatchInvoices from "@/pages/DispatchInvoices";
import DispatchRequests from "@/pages/DispatchRequests";
import Backups from "@/pages/Backups";
import BankSettings from "@/pages/BankSettings";
import HeliopolisWebsite from "@/pages/website/HeliopolisWebsite";
import Enquiries from "@/pages/Enquiries";
import ExternalAwbs from "@/pages/ExternalAwbs";
import AwbRequests from "@/pages/AwbRequests";
import BoxLabels from "@/pages/BoxLabels";
import ConsolidatedInvoiceDetail from "@/pages/ConsolidatedInvoiceDetail";
import TransitShipments from "@/pages/TransitShipments";

const homeFor = (user) =>
  user?.role === "dispatch" ? "/dispatch" : user?.role === "client_ops" ? "/portal" : "/dashboard";

function Spinner() {
  return (
    <div className="h-screen flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleRoute({ allow, children }) {
  const { user } = useAuth();
  if (user && !allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RootGate() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return <HeliopolisWebsite />;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const team = ["team", "admin"];
  const teamClient = ["team", "admin", "client"];
  const teamDispatch = ["team", "admin", "dispatch"];
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/login" element={<Login />} />
      <Route path="/c/heliopolis" element={<PublicCatalogue />} />
      <Route
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route path="/portal/*" element={<RoleRoute allow={["client_ops"]}><ClientOpsPortal /></RoleRoute>} />
        <Route path="/dashboard" element={user?.role === "dispatch" ? <Navigate to="/dispatch" replace /> : user?.role === "client_ops" ? <Navigate to="/portal" replace /> : <Dashboard />} />
        <Route path="/reports" element={<RoleRoute allow={teamClient}><Reports /></RoleRoute>} />
        <Route path="/orders" element={<RoleRoute allow={teamClient}><Orders /></RoleRoute>} />
        <Route path="/orders/new" element={<RoleRoute allow={teamClient}><PlaceOrder /></RoleRoute>} />
        <Route path="/orders/:id/edit" element={<RoleRoute allow={teamClient}><PlaceOrder /></RoleRoute>} />
        <Route path="/orders/:id" element={<RoleRoute allow={teamClient}><OrderDetail /></RoleRoute>} />
        <Route path="/shipments" element={<RoleRoute allow={teamClient}><Shipments /></RoleRoute>} />
        <Route path="/shipments/:id" element={<RoleRoute allow={teamClient}><ShipmentDetail /></RoleRoute>} />
        <Route path="/client-confirmations" element={<RoleRoute allow={team}><ClientConfirmations /></RoleRoute>} />
        <Route path="/purchase-orders" element={<RoleRoute allow={team}><PurchaseOrders /></RoleRoute>} />
        <Route path="/purchase-orders/:id" element={<RoleRoute allow={team}><PurchaseOrderDetail /></RoleRoute>} />
        <Route path="/suppliers" element={<RoleRoute allow={team}><Suppliers /></RoleRoute>} />
        <Route path="/clients" element={<RoleRoute allow={team}><Clients /></RoleRoute>} />
        <Route path="/enquiries" element={<RoleRoute allow={team}><Enquiries /></RoleRoute>} />
        <Route path="/external-awbs" element={<RoleRoute allow={team}><ExternalAwbs /></RoleRoute>} />
        <Route path="/awb-requests" element={<RoleRoute allow={team}><AwbRequests /></RoleRoute>} />
        <Route path="/box-labels" element={<RoleRoute allow={team}><BoxLabels /></RoleRoute>} />
        <Route path="/transit-shipments" element={<RoleRoute allow={team}><TransitShipments /></RoleRoute>} />
        <Route path="/users" element={<RoleRoute allow={["admin"]}><UsersPage /></RoleRoute>} />
        <Route path="/invoices" element={<RoleRoute allow={teamClient}><Invoices /></RoleRoute>} />
        <Route path="/invoices/:id" element={<RoleRoute allow={teamClient}><InvoiceDetail /></RoleRoute>} />
        <Route path="/consolidated-invoices/:id" element={<RoleRoute allow={teamClient}><ConsolidatedInvoiceDetail /></RoleRoute>} />
        <Route path="/catalogue" element={<RoleRoute allow={teamClient}><Catalogue /></RoleRoute>} />
        <Route path="/stock" element={<RoleRoute allow={team}><Stock /></RoleRoute>} />
        <Route path="/schedule" element={<RoleRoute allow={team}><Schedule /></RoleRoute>} />
        <Route path="/quotes" element={<RoleRoute allow={team}><Quotes /></RoleRoute>} />
        <Route path="/quotes/:id" element={<RoleRoute allow={team}><QuoteDetail /></RoleRoute>} />
        <Route path="/dispatch" element={<RoleRoute allow={teamDispatch}><Dispatch /></RoleRoute>} />
        <Route path="/stock-intake" element={<RoleRoute allow={teamDispatch}><StockIntake /></RoleRoute>} />
        <Route path="/dispatch/:id" element={<RoleRoute allow={teamDispatch}><DispatchDetail /></RoleRoute>} />
        <Route path="/dispatch-invoices" element={<RoleRoute allow={teamDispatch}><DispatchInvoices /></RoleRoute>} />
        <Route path="/dispatch-invoices/:id" element={<RoleRoute allow={teamDispatch}><DispatchInvoices /></RoleRoute>} />
        <Route path="/dispatch-requests" element={<RoleRoute allow={teamDispatch}><DispatchRequests /></RoleRoute>} />
        <Route path="/backups" element={<RoleRoute allow={["admin"]}><Backups /></RoleRoute>} />
        <Route path="/settings" element={<RoleRoute allow={["admin"]}><BankSettings /></RoleRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
            <Toaster position="bottom-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
